/**
 * AuthGate — Firebase 개인 로그인 게이트 + 역할 컨텍스트 (2026-07-24 완전 전환).
 * VITE_AUTH=firebase 일 때 App 을 감싼다:
 *   미로그인 → 로그인 화면(가입은 초대 링크로만) · 로그인+미승인 → 대기 화면 ·
 *   승인 → AuthContext(역할) 로 감싼 children(앱).
 * 승인 상태·역할은 백엔드 /api/staff/me(app_users). 세션은 auth/session.js.
 */
import { useCallback, useEffect, useState } from "react";
import { API_BASE_URL, apiFetch } from "../utils";
import {
  AUTH_ENABLED, currentEmail, getEmailVerificationState, isSignedIn, signIn, signOut,
} from "./session";
import { AuthContext } from "./AuthContext";
import { DEFAULT_ACCESS, mergeAccess, DEFAULT_DENY, mergeDeny } from "./access";
import GoogleButton from "./GoogleButton";
import EmailVerificationGate from "./EmailVerificationGate";

export { AUTH_ENABLED };

export function useAuthSession() {
  const [signedIn, setSignedIn] = useState(() => isSignedIn());
  useEffect(() => {
    const on = () => setSignedIn(isSignedIn());
    window.addEventListener("iggg-auth-changed", on);
    return () => window.removeEventListener("iggg-auth-changed", on);
  }, []);
  return { signedIn, email: currentEmail(), signOut };
}

// ⚠ 로그인 화면은 고정 다크 디자인 — 테마 변수(var(--ds-*)) 금지.
// 앱 전역 CSS 가 라이트일 때 --ds-ink 가 어두운 글자로 잡혀 다크 카드 위에서 안 보였던
// 가독성 버그(2026-07-24) 재발 방지. 색은 전부 명시 고정.
const INK = "#eef1f6", INK_DIM = "#aab3c0", BG = "#0f1115", SURFACE = "#1b2029",
      LINE = "#39414f", ACCENT = "#4c8dff";
const wrap = {
  minHeight: "100vh", display: "flex", alignItems: "center",
  justifyContent: "center", background: BG, color: INK, padding: 24,
};
const card = {
  width: "100%", maxWidth: 360, display: "flex", flexDirection: "column",
  gap: 12, padding: 28, borderRadius: 16, background: SURFACE,
  border: `1px solid ${LINE}`, color: INK,
};
const inp = {
  padding: "11px 13px", borderRadius: 10, border: `1px solid ${LINE}`,
  background: "#0f131a", color: INK, fontSize: 15, outline: "none",
};
const btn = (busy) => ({
  padding: "10px 12px", borderRadius: 10, border: "none",
  background: "#4c8dff", color: "#fff", fontWeight: 600,
  cursor: busy ? "wait" : "pointer", opacity: busy ? 0.7 : 1,
});

/** 로그인 전용 화면. 가입은 초대 링크(/join/:token)로만 — 여기엔 가입 버튼 없음. */
function AuthForm() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      await signIn(email.trim(), password);
    } catch (err) {
      setError(err?.message || "로그인 실패");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={wrap}>
      <div style={card}>
        <h1 style={{ fontFamily: "var(--ff-brand, monospace)", fontSize: 22, fontWeight: 700, margin: "0 0 2px", letterSpacing: "-0.02em" }}>
          ig <span style={{ fontWeight: 500, opacity: 0.7 }}>works</span>
        </h1>

        <GoogleButton onError={(e) => setError(e?.message || "구글 로그인 실패")} />
        <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#8b95a3", fontSize: 12 }}>
          <span style={{ flex: 1, height: 1, background: "#39414f" }} /> 또는 <span style={{ flex: 1, height: 1, background: "#39414f" }} />
        </div>

        <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <input type="email" required autoFocus placeholder="이메일" value={email}
                 onChange={(e) => setEmail(e.target.value)} autoComplete="username" style={inp} />
          <input type="password" required placeholder="비밀번호" value={password}
                 onChange={(e) => setPassword(e.target.value)}
                 autoComplete="current-password" style={inp} />
          {error && <p style={{ margin: 0, fontSize: 13, color: "#f28b82" }}>{error}</p>}
          <button type="submit" disabled={busy} style={btn(busy)}>
            {busy ? "로그인 중…" : "로그인"}
          </button>
        </form>

        <p style={{ margin: 0, fontSize: 12, opacity: 0.6, textAlign: "center", lineHeight: 1.5 }}>
          가입은 관리자가 보낸 <b>초대 링크</b>로만 가능합니다.
        </p>
      </div>
    </div>
  );
}

function PendingScreen({ status, onSignOut }) {
  // (전수검증) status 'none'(미초대·탈퇴)을 'pending'과 구분 — 승인 대기로 오인한 영구 대기 늪 방지.
  const isBlocked = status === "blocked";
  const isNone = status === "none";
  const title = isBlocked ? "접근 정지" : isNone ? "초대가 필요합니다" : "승인 대기";
  const msg = isBlocked
    ? "이 계정은 접근이 정지되었습니다. 관리자에게 문의하세요."
    : isNone
    ? "이 계정은 아직 초대되지 않았습니다. 가입은 관리자가 보낸 초대 링크로만 가능합니다. 관리자에게 초대 링크를 요청한 뒤, 받은 링크로 다시 접속해 주세요."
    : "관리자 승인 대기 중입니다. 승인되면 바로 이용할 수 있습니다.";
  return (
    <div style={wrap}><div style={card}>
      <h1 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>{title}</h1>
      <p style={{ margin: 0, fontSize: 13, opacity: 0.8 }}>{msg}</p>
      <p style={{ margin: 0, fontSize: 12.5, opacity: 0.6 }}>{currentEmail()}</p>
      <button type="button" style={btn(false)} onClick={onSignOut}>로그아웃</button>
    </div></div>
  );
}

/** 로그인 후 승인·역할 확인 게이트. App 을 감싼다. */
export function AuthGate({ children }) {
  const { signedIn } = useAuthSession();
  const [state, setState] = useState({ loading: true, status: null, role: null, access: DEFAULT_ACCESS });

  const check = useCallback(async () => {
    if (!signedIn) { setState({ loading: false, status: null, role: null, access: DEFAULT_ACCESS }); return; }
    setState((s) => ({ ...s, loading: true }));
    try {
      const verification = await getEmailVerificationState();
      if (!verification.signedIn) {
        setState({ loading: false, status: null, role: null, access: DEFAULT_ACCESS });
        return;
      }
      if (verification.requiresVerification) {
        setState({
          loading: false,
          status: "email_unverified",
          role: null,
          access: DEFAULT_ACCESS,
          deny: DEFAULT_DENY,
        });
        return;
      }

      const meRes = await apiFetch(`${API_BASE_URL}/api/staff/me`);
      const d = await meRes.json().catch(() => ({}));
      if (!meRes.ok && d?.detail?.code === "EMAIL_NOT_VERIFIED") {
        setState({
          loading: false,
          status: "email_unverified",
          role: null,
          access: DEFAULT_ACCESS,
          deny: DEFAULT_DENY,
        });
        return;
      }
      const permRes = meRes.ok
        ? await apiFetch(`${API_BASE_URL}/api/staff/permissions`).catch(() => null)
        : null;
      let access = DEFAULT_ACCESS;
      let deny = DEFAULT_DENY;
      try {
        if (permRes && permRes.ok) {
          const cfg = (await permRes.json()).access;
          access = mergeAccess(cfg);
          deny = mergeDeny(cfg);   // 기능별 역할 제외(#56 — 진현은 계약 계열 제외)
        }
      } catch { /* 실패 시 기본값 */ }
      setState({ loading: false, status: d.status || "none", role: d.role || null, access, deny });
    } catch {
      setState({ loading: false, status: "error", role: null,
                 access: DEFAULT_ACCESS, deny: DEFAULT_DENY });
    }
  }, [signedIn]);
  useEffect(() => { check(); }, [check]);

  if (!signedIn) return <AuthForm />;
  if (state.loading) {
    return <div style={wrap}><div style={{ opacity: 0.6 }}>확인 중…</div></div>;
  }
  if (state.status === "email_unverified") {
    return <EmailVerificationGate onVerified={check} />;
  }
  if (state.status === "approved") {
    return (
      <AuthContext.Provider value={{
        role: state.role || "staff", email: currentEmail(),
        status: "approved", authEnabled: true,
        access: state.access, deny: state.deny, refresh: check,
      }}>
        {children}
      </AuthContext.Provider>
    );
  }
  if (state.status === "error") {
    return (
      <div style={wrap}><div style={card}>
        <p style={{ margin: 0, fontSize: 13 }}>서버에 연결할 수 없습니다.</p>
        <button type="button" style={btn(false)} onClick={check}>다시 시도</button>
        <button type="button" style={{ ...inp, cursor: "pointer" }} onClick={() => signOut()}>로그아웃</button>
      </div></div>
    );
  }
  return <PendingScreen status={state.status} onSignOut={() => signOut()} />;
}

// 하위호환: 기존 App.jsx 가 LoginPage 를 직접 쓰던 경로.
export function LoginPage() { return <AuthForm />; }
